// ─── PROMPT BUILDER FOR REAL-ROOM VIRTUAL STAGING ────────────────────────────
//
// Separate from the component so it can be read, reasoned about and tested
// without mounting any UI. It is a pure function: same inputs, same string.
//
// This is a DIFFERENT job from the 3D render prompt in DownloadModal. There the
// whole point is that nothing may change — the model must reproduce a design the
// user already built. Here the user hands over a photograph of a real room and
// asks for furniture that was never there. So the two prompts pull in opposite
// directions, and mixing them would break both.
//
// What stays fixed either way is the ARCHITECTURE. A staged photo is worthless
// if the room is no longer the user's room, so the shell — walls, windows,
// doors, floor, ceiling, camera — is frozen exactly as hard here as in the
// render prompt, and stated first for the same reason: this model follows what
// comes first far more reliably than what comes last.

export const ROOM_TYPES = [
  // ── `auto` IS FIRST, AND SO THE DEFAULT ────────────────────────────────────
  // See the same note on DESIGN_STYLES below; the reasoning is stronger here.
  // The default used to be "Living Room", so anyone who uploaded a photo of
  // their bedroom and never opened this dropdown was asking for a sofa, a
  // coffee table and a media console to be put in it. A wrong room type is not
  // a matter of taste — it is furniture that does not belong in the room.
  //
  // The model can SEE the photograph, so it can tell a bedroom from a kitchen
  // more reliably than a default can guess.
  //
  // `furniture: null` is what marks the choice as delegated, exactly as
  // `desc: null` does for the style and the palette — everything downstream
  // branches on the absence of the field rather than on the id, so there is
  // one mechanism and not three special cases.
  //
  // `label` is still a real word because it is interpolated into the opening
  // sentence: "restyle … into a modern minimalist ROOM" reads correctly, where
  // a label of "Auto (AI chooses)" would not.
  { id: 'auto',    label: 'Room',        furniture: null },

  { id: 'living',  label: 'Living Room', furniture: 'a sofa, armchairs, a coffee table, a rug, a media console and table lamps' },
  { id: 'bedroom', label: 'Bedroom',     furniture: 'a made bed with pillows, bedside tables with lamps, a dresser and a rug' },
  { id: 'kitchen', label: 'Kitchen',     furniture: 'bar stools at the counter, small appliances, a fruit bowl and open shelving with crockery' },
  { id: 'bathroom', label: 'Bathroom',   furniture: 'folded towels, a bath mat, plants and simple toiletries on the vanity' },
  { id: 'dining',  label: 'Dining Room', furniture: 'a dining table with chairs, a table centrepiece, a sideboard and a pendant light' },
  { id: 'office',  label: 'Home Office', furniture: 'a desk, an ergonomic chair, a bookshelf, a desk lamp and framed art' },

  // `furniture` IS THE PROMPT, not a description of one. It is dropped into the
  // instruction verbatim, so each list names concrete objects a model can place
  // — "a low single bed, a small desk" — rather than a mood. Vague entries here
  // are where a staged room comes back looking like a showroom for nothing in
  // particular.
  { id: 'kids',     label: "Kids' Room",   furniture: 'a low single bed with colourful bedding, a small desk and chair, open toy storage, a soft rug and a few soft toys' },
  { id: 'nursery',  label: 'Nursery',      furniture: 'a cot, a changing table, a nursing armchair, a soft rug, a low shelf of picture books and a plush toy' },
  { id: 'teen',     label: 'Teen Room',    furniture: 'a single or day bed, a study desk with a chair and monitor, open shelving, a bean bag and wall posters' },
  { id: 'guest',    label: 'Guest Room',   furniture: 'a neatly made double bed, a bedside table with a lamp, a luggage bench, a slim wardrobe and framed art' },
  { id: 'hallway',  label: 'Hallway / Entryway', furniture: 'a slim console table, a mirror, a coat rack, a shoe bench and a runner rug' },
  { id: 'studio',   label: 'Studio Apartment',   furniture: 'a sofa bed, a compact dining table with two chairs, open shelving as a room divider, a rug and floor lamps' },
  { id: 'dressing', label: 'Dressing Room',      furniture: 'open wardrobe systems with hanging rails, a full-length mirror, a padded stool, drawer units and neat shoe shelving' },
  { id: 'balcony',  label: 'Balcony / Loggia',   furniture: 'a compact bistro table with two chairs, an outdoor rug, potted plants and a floor lantern' },
];

// ── `auto` IS FIRST, AND SO THE DEFAULT ──────────────────────────────────────
// Most people opening this form want a nicer room, not a particular movement,
// and being made to pick one before seeing anything is a decision taken with no
// information. `auto` hands that choice to the model, which can at least see
// the photograph.
//
// It carries NO `desc`. Everything downstream tests for that rather than for
// the id, so the prompt branches on "was a style chosen" instead of on a magic
// string — and the same mechanism serves the palette without a second special
// case. A named style is an instruction to obey; `auto` is a brief.
export const DESIGN_STYLES = [
  { id: 'auto', label: 'Auto (AI chooses)', desc: null },
  { id: 'modern_minimalist', label: 'Modern Minimalist', desc: 'modern minimalist: clean lines, uncluttered surfaces, low-profile furniture, no ornament' },
  { id: 'scandinavian',      label: 'Scandinavian',      desc: 'Scandinavian: pale wood, soft textiles, simple functional shapes, airy and uncluttered' },
  { id: 'luxury_modern',     label: 'Luxury Modern',     desc: 'luxury modern: rich materials, marble and brass accents, deep upholstery, sculptural statement pieces' },
  { id: 'japandi',           label: 'Japandi',           desc: 'Japandi: low natural-wood furniture, handmade ceramics, restrained palette, calm negative space' },
  { id: 'industrial',        label: 'Industrial',        desc: 'industrial: blackened metal frames, leather, raw timber, exposed hardware' },
  { id: 'boho',              label: 'Boho',              desc: 'boho: layered rugs and textiles, rattan and cane, abundant plants, relaxed eclectic mix' },

  // The ids below deliberately match the vocabulary in constants/designer.js,
  // so "Neoclassic" means the same thing to a designer filling in their profile
  // and to someone staging a photograph.
  { id: 'classic',      label: 'Classic',           desc: 'classic traditional: symmetrical arrangements, turned wood, rolled arms, patterned upholstery, warm layered lighting' },
  { id: 'neoclassic',   label: 'Neoclassic',        desc: 'neoclassical: panelled walls, fluted detail, restrained mouldings, pale stone and muted gilt accents' },
  { id: 'contemporary', label: 'Contemporary',      desc: 'contemporary: current shapes, mixed textures, soft curves, calm palette with one sculptural piece' },
  { id: 'mid_century',  label: 'Mid-Century Modern', desc: 'mid-century modern: tapered walnut legs, low slung seating, muted mustard and teal, globe and arc lighting' },
  { id: 'art_deco',     label: 'Art Deco',          desc: 'art deco: fluted and scalloped forms, brass and lacquer, geometric inlay, velvet upholstery, bold symmetry' },
  { id: 'mediterranean', label: 'Mediterranean',    desc: 'Mediterranean: whitewashed and lime-plaster surfaces, terracotta, woven textures, olive and sea-blue accents' },
  { id: 'rustic',       label: 'Rustic / Farmhouse', desc: 'rustic farmhouse: reclaimed and knotted timber, wrought iron, linen, handmade ceramics, soft worn finishes' },
  { id: 'provence',     label: 'Provence',          desc: 'Provence: painted and distressed wood, lavender and soft blue accents, floral linens, gentle daylight' },
  { id: 'loft',         label: 'Loft',              desc: 'loft: open volume, exposed brick and ductwork, oversized metal-framed pieces, leather and raw steel' },
  { id: 'high_tech',    label: 'High-Tech',         desc: 'high-tech: glass and polished metal, hidden storage, integrated linear lighting, monochrome and precise' },
];

export const COLOR_PALETTES = [
  // See the note on DESIGN_STYLES — `desc: null` is what marks it as a choice
  // deferred to the model rather than one the reader has made.
  { id: 'auto', label: 'Auto (AI chooses)', desc: null },
  { id: 'warm_neutrals',   label: 'Warm Neutrals',      desc: 'warm neutrals — beige, cream, oatmeal, soft tan' },
  { id: 'cool_greys',      label: 'Cool Greys & Blues', desc: 'cool greys and blues — slate, dove grey, muted navy' },
  { id: 'earthy',          label: 'Earthy Tones',       desc: 'earthy tones — terracotta, ochre, olive, clay' },
  { id: 'white_lightwood', label: 'White & Light Wood', desc: 'white with light wood — off-white textiles, pale oak and birch' },
  { id: 'bold_dark',       label: 'Bold Dark Accent',   desc: 'a mostly neutral scheme with one bold dark accent — charcoal, ink blue or forest green' },

  // Each names ACTUAL COLOURS, not a feeling. "Jewel tones" alone gives the
  // model nothing to reach for; naming emerald, sapphire and garnet does.
  { id: 'monochrome',    label: 'Monochrome',        desc: 'monochrome — black, white and the greys between, with texture doing the work instead of colour' },
  { id: 'greige',        label: 'Greige & Taupe',    desc: 'greige and taupe — warm grey, mushroom, stone and putty' },
  { id: 'sage',          label: 'Sage & Olive',      desc: 'muted greens — sage, olive and eucalyptus against off-white' },
  { id: 'blush',         label: 'Blush & Dusty Rose', desc: 'soft pinks — blush, dusty rose and clay against warm white' },
  { id: 'jewel',         label: 'Jewel Tones',       desc: 'jewel tones — emerald, sapphire, garnet and amethyst, deep and saturated' },
  { id: 'navy_brass',    label: 'Navy & Brass',      desc: 'deep navy with brass and gold accents over cream' },
  { id: 'pastel',        label: 'Soft Pastels',      desc: 'soft pastels — powder blue, pale mint, buttermilk and light lilac' },
  { id: 'walnut_cream',  label: 'Walnut & Cream',    desc: 'dark walnut and cream — rich brown timber against warm off-white' },
];

// FURNISHED IS FIRST, AND SO IS THE DEFAULT.
//
// Getting this wrong is the single most damaging mistake in the form, and it was
// the easy one to make: a user uploads a photo of their furnished bedroom, never
// touches the dropdown, and the prompt tells the model "furnish this EMPTY
// bedroom". Being told the room is empty when it plainly is not, the model stops
// editing the photo and starts imagining an empty room instead — so the walls
// move, the size changes and a window appears that was never there.
//
// "Furnished" is safe in both directions. On a furnished room it does the right
// thing; on an empty one, "remove all existing furniture, then furnish" simply
// has nothing to remove. "Empty" is only safe on a genuinely empty room, so it
// must never be what someone gets by accident.
export const ROOM_STATES = [
  { id: 'furnished', label: 'Furnished (Redesign / Redecorate)' },
  { id: 'empty',     label: 'Empty Space (Full Staging)' },
];

// ── WHAT MAY BE REDESIGNED ───────────────────────────────────────────────────
// Five checkboxes, replacing a two-way "renovation state" that could only say
// "keep every surface" or "redo every surface".
//
// THE BINARY WAS WRONG IN BOTH POSITIONS. "Finished" froze the floor, so a
// concrete screed came back as the same concrete screed; "unfinished" repainted
// the walls and stopped there, because that is all the sentence asked for. And
// neither could say anything at all about doors and windows — those were frozen
// by the geometry rule below, so a photograph with a battered steel gate in it
// always came back with the same battered steel gate.
//
// THE DISTINCTION THAT MAKES DOORS POSSIBLE. An opening is a hole in a wall; a
// door is the thing hanging in it. Freezing the first is what keeps the room
// the user's room. Freezing the second was an accident of them being described
// in one sentence — and it is also why "do not add a door" was unreliable: a
// blanket freeze that the model can see is being partly ignored (the walls DID
// change colour) is weaker than a precise rule it can follow exactly.
//
// So each entry carries both halves. `change` is what to do when the box is
// ticked, `keep` when it is not — written as instructions rather than as
// labels, because that is what they are.
export const SURFACES = [
  // `change` and `keep` are PHRASES, composed into one instruction each below.
  // They used to be a paragraph apiece — five of them put roughly 200 words of
  // surface policy into an instruction that was already far too long to act on.
  // See the note above buildRealStagingPrompt.
  // ── NAME THE MATERIALS ────────────────────────────────────────────────────
  // These were "a completely new floor finish" and "properly finished walls",
  // and both came back as a coat of grey paint over the same concrete and the
  // same plaster. It is the furniture failure again: "new" is satisfied by a
  // recolour, and the only thing that forces a real change is naming what the
  // new thing could be MADE of. A vocabulary is something to render; "finish
  // the walls properly" is an adjective.
  // ── ONE SHORT SENTENCE EACH, NOT FIVE CLAUSES IN ONE ──────────────────────
  // These were phrases joined with commas into a single ~90-word sentence, and
  // five real generations came back with the floor and walls done and the DOOR
  // — the fourth clause — merely repainted. It is the same failure "redo the
  // walls, floor and ceiling" had: one sentence reads as one instruction, and
  // the model satisfies the beginning of it.
  //
  // So each is its own sentence, and each carries its own "not recoloured"
  // rather than relying on a summary line after all five. Short sentences, not
  // the paragraphs these were two revisions ago — the point is separation, not
  // length.
  {
    id: 'floor',
    change: 'New floor: large-format tile, natural stone, marble, engineered'
      + ' timber, parquet or polished concrete — a different material, not the'
      + ' old floor recoloured.',
    keep: 'the floor',
  },
  {
    id: 'walls',
    change: 'New wall surfaces: panelling, slatted timber, stone or tile'
      + ' cladding, microcement, textured plaster or wallpaper — not simply'
      + ' repainted.',
    keep: 'the wall finishes',
  },
  {
    id: 'ceiling',
    change: 'New ceiling: slats, coffers or a dropped feature with integrated'
      + ' lighting.',
    keep: 'the ceiling',
  },
  // The two that were impossible before the opening/unit split. Both name the
  // opening inside the phrase, so "new door" is never read on its own.
  // The door needed the same treatment the floor and walls did: "a new door"
  // was being satisfied by the old door in a new colour, so the leaf styles
  // are named the way the floor's materials are.
  {
    // THE ONE THAT SURVIVED FIVE GENERATIONS UNCHANGED. Every result kept the
    // same two-panel glazed double door and repainted it — gold, dark, beige.
    // It is stated first among the openings, in its own sentence, and the
    // failure is named in the words the picture kept showing.
    id: 'doors',
    change: 'New doors: a completely different door design — a different leaf'
      + ' style, material and handle (flush, panelled, slatted, glazed or'
      + ' full-height) — hung in the same openings. The old door repainted is'
      + ' not a new door.',
    keep: 'the doors',
  },
  {
    id: 'windows',
    change: 'New window frames and profiles in the same openings.',
    keep: 'the windows',
  },
];

export const SURFACE_IDS = SURFACES.map((s) => s.id);

const byId = (list, id) => list.find((x) => x.id === id) || list[0];

// "a industrial room", "a art deco bedroom" — the opening sentence interpolates
// a style name after a hardcoded "a", and two of the styles begin with a vowel.
// Sloppy English in the first line of a prompt is not free: this model weights
// the opening most, and it is the one sentence naming the whole job.
//
// The vowel-LETTER test is an approximation, and a correct one for this list —
// there is no "a European" or "an hour" among the styles. If one is ever added,
// this is where it goes.
const article = (word) => (/^[aeiou]/i.test(word) ? 'an' : 'a');

// ── A HEX IS A WEAK INSTRUCTION; A NAME IS A STRONG ONE ──────────────────────
// A reader picked #2E4A3D and #7A8296 and got wood panelling and blue chairs.
// Part of that is that an image model renders "a deep forest green" far more
// reliably than it renders six hex digits — the code is a precise value it has
// no particular training signal for, where the name is a thing it has seen ten
// million pictures of.
//
// So both go in: the name to steer, the hex to disambiguate. Nearest match by
// plain RGB distance over a small vocabulary of interior colours — not a
// perceptually uniform space, which would be better and is not worth a
// dependency for choosing between thirty words.
const COLOR_NAMES = [
  ['#000000', 'near-black'], ['#2B2B2B', 'charcoal'], ['#6E6E6E', 'mid grey'],
  ['#B8B8B8', 'light grey'], ['#FFFFFF', 'white'], ['#F2EDE4', 'off-white cream'],
  ['#E8DCC8', 'warm sand'], ['#C8B89A', 'oatmeal'], ['#8B7355', 'taupe brown'],
  ['#5C4033', 'dark walnut brown'], ['#A0522D', 'terracotta'], ['#D2691E', 'burnt orange'],
  ['#8B0000', 'deep red'], ['#B03060', 'raspberry'], ['#E8B4B8', 'blush pink'],
  ['#4B0082', 'deep purple'], ['#7A8296', 'slate blue-grey'], ['#36454F', 'charcoal blue'],
  ['#1F3A5F', 'deep navy'], ['#4682B4', 'steel blue'], ['#87CEEB', 'pale sky blue'],
  ['#2E4A3D', 'deep forest green'], ['#556B2F', 'olive green'], ['#8A9A5B', 'sage green'],
  ['#B2BEB5', 'pale eucalyptus'], ['#B8860B', 'antique gold'], ['#C9A227', 'brass gold'],
  ['#DAA520', 'mustard'], ['#E5E4E2', 'platinum'],
];

function nameOfHex(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  let best = COLOR_NAMES[0];
  let bestD = Infinity;
  for (const entry of COLOR_NAMES) {
    const [h] = entry;
    const d = (parseInt(h.slice(1, 3), 16) - r) ** 2
      + (parseInt(h.slice(3, 5), 16) - g) ** 2
      + (parseInt(h.slice(5, 7), 16) - b) ** 2;
    if (d < bestD) { bestD = d; best = entry; }
  }
  return best[1];
}

// Anything a user types goes into a prompt, so it is length-limited and stripped
// of the characters that would let it read as a new instruction block rather
// than as a request inside this one.
function sanitiseNotes(notes) {
  return String(notes || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/["'`{}<>[\]]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .slice(0, 300);
}

/**
 * Build the staging prompt.
 *
 * @param {object}  o
 * @param {string}  o.roomType     id from ROOM_TYPES
 * @param {string}  o.designStyle  id from DESIGN_STYLES
 * @param {string}  o.colorPalette id from COLOR_PALETTES
 * @param {string}  o.roomState    'empty' | 'furnished'
 * @param {string} [o.customNotes] free text from the user
 */
export function buildRealStagingPrompt(o = {}) {
  const room = byId(ROOM_TYPES, o.roomType);

  // ── `auto` PICKS ONE, RATHER THAN ASKING THE MODEL TO ─────────────────────
  // It used to emit a brief — "choose a single coherent interior style that
  // suits this room and commit to it fully". That reads well and works badly:
  // it adds thirty words of meta-instruction and gives the model nothing
  // concrete to render, so an `auto` run consistently changed LESS than a run
  // with a named style. Which is the opposite of what "auto" promises.
  //
  // So the app chooses. A concrete description — "Japandi: low natural-wood
  // furniture, handmade ceramics, restrained palette" — is something to draw;
  // "pick a style" is not.
  //
  // RANDOM, not fixed. Two runs of `auto` on the same photo should differ —
  // that is what someone pressing generate again is asking for — and a fixed
  // pick would make `auto` a synonym for one style.
  const pickReal = (list) => {
    const real = list.filter((x) => x.desc);
    return real[Math.floor(Math.random() * real.length)];
  };
  const chosenStyle = byId(DESIGN_STYLES, o.designStyle);
  const style = chosenStyle.desc ? chosenStyle : pickReal(DESIGN_STYLES);
  const isEmpty = (o.roomState || ROOM_STATES[0].id) === 'empty';
  const notes = sanitiseNotes(o.customNotes);

  // ── WHICH SURFACES MAY CHANGE ─────────────────────────────────────────────
  // An ARRAY of ids from the caller. Undefined means "all of them", which is
  // both the form's default and the right answer for a caller that predates
  // this argument — the old `finishState: 'unfinished'` meant roughly this.
  const allowed = new Set(
    Array.isArray(o.surfaces) ? o.surfaces.filter((s) => SURFACE_IDS.includes(s))
      : SURFACE_IDS,
  );
  const mayChange = SURFACES.filter((s) => allowed.has(s.id));
  const mustKeep = SURFACES.filter((s) => !allowed.has(s.id));

  // Same for the palette — see the note on the style above.
  const chosenPalette = byId(COLOR_PALETTES, o.colorPalette);
  const palette = chosenPalette.desc ? chosenPalette : pickReal(COLOR_PALETTES);

  // ── CHOSEN, OR DELEGATED ──────────────────────────────────────────────────
  // A `desc` of null means the reader picked `auto`, so the model is briefed
  // instead of instructed. The brief still constrains: "choose one and hold to
  // it" is what stops a delegated choice becoming no choice at all — a room
  // furnished in three styles at once, which is what "any style you like"
  // reliably produces.
  // ── TWO COLOURS THE READER PICKED ─────────────────────────────────────────
  // When they choose their own pair it REPLACES the palette rather than joining
  // it: "warm neutrals, and also these two greens" is two palettes, and mixing
  // palettes is the thing every other line here is trying to prevent.
  //
  // The hex codes go in verbatim AND are described as a role each — a model
  // renders "#2E4A3D" less reliably than it renders "a deep green as the
  // dominant colour", and having both means the pair is unambiguous whichever
  // it reads.
  const hexes = (Array.isArray(o.customColors) ? o.customColors : [])
    .filter((c) => /^#[0-9a-f]{6}$/i.test(String(c)));
  const colour = hexes.length === 2
    ? `Colours: build the entire scheme from exactly two — ${nameOfHex(hexes[0])}`
      + ` (${hexes[0]}) as the dominant colour on the large surfaces, and`
      + ` ${nameOfHex(hexes[1])} (${hexes[1]}) as the accent on furniture and`
      + ' detail. Neutrals may support them. Use no other colour family, and'
      + ' IGNORE any colours named in the style above — these two replace them.'
      // "joinery" used to be in that accent list, and joinery is doors and
      // window frames — so a reader who had unticked Doors still got theirs
      // repainted in the accent colour. The scope of the scheme is now the
      // `keepLine` below's business, and that line names what it does not
      // touch.
    : `Colours: ${palette.desc}.`;

  // ── WHAT TO PUT IN THE ROOM ───────────────────────────────────────────────
  // A named room type hands over a concrete list; `auto` (no `furniture`) asks
  // the model to work the room out from the photograph first, which it is well
  // placed to do — a bed, a hob or a bath is visible even in an empty room's
  // plumbing and sockets.
  //
  // The brief still CONSTRAINS, for the same reason the style brief does: "one
  // room type, chosen and committed to" is what stops a delegated choice
  // becoming no choice — a bed and a kitchen island in the same picture is
  // what "furnish it appropriately" reliably produces.
  // "as a bedroom " or nothing. Trailing space so the caller reads
  // `furnish it ${asRoom}with …` without a double space when it is empty.
  //
  // USED AS AN ADVERB, never as the object of the sentence. "Furnish it as a
  // bedroom" says what the space is for; "convert it into a bedroom" — which
  // this replaced — was read as a request to build one, and reshaped the room.
  const asRoom = room.furniture ? `as ${article(room.label)} ${room.label.toLowerCase()} ` : '';

  const furnishWith = room.furniture
    ? room.furniture
    : 'the furniture that this room is actually for — first work out from the'
      + ' photograph what kind of room it is (its windows, plumbing, sockets,'
      + ' built-ins and proportions), commit to that one room type, and furnish'
      + ' it with the pieces that belong in it and nothing from another kind of'
      + ' room';

  // ── HOW LONG THIS MAY BE ──────────────────────────────────────────────────
  // The version before this one ran to 687 words, 34% of them preservation
  // language — "keep", "same", "unchanged", "do not" — and the first sixty
  // words were "not a new room… keep the room itself completely unchanged".
  //
  // Kontext is an INSTRUCTION-EDIT model with no strength dial (see
  // generateImageFlux.js): how much of the photo survives is decided entirely
  // by what the instruction asks for. Handed a long instruction that is mostly
  // constraints, the cheapest way to satisfy most of its tokens is to change
  // almost nothing — which is exactly what came back. One real generation
  // removed the clutter and added a single cabinet; another, asked for "luxury
  // modern" and "bold dark accent", repainted the CAR black and left the room
  // alone.
  //
  // So: short, and led by what must change. The guards are kept but compressed
  // to one sentence each and moved after the work, where they read as
  // qualifications on a job rather than as the job itself.
  // ── UNTICKED MEANS UNTOUCHED, COLOUR INCLUDED ─────────────────────────────
  // "Leave untouched: the doors." was being read as "do not replace the door"
  // while the colour instruction two sentences earlier said to put the accent
  // colour on the joinery — so an unticked door came back the same door in the
  // new scheme's colour. Which is a change, and the reader had asked for none.
  //
  // Saying it explicitly costs one clause and removes the ambiguity: the scheme
  // has a scope, and these are outside it.
  // The section heading is added at the join now, so this is the body only —
  // otherwise the output reads "LEAVE UNTOUCHED: Leave untouched: …".
  const keepLine = mustKeep.length
    ? `${mustKeep.map((s) => s.keep).join(', ')}. Keep their`
      + ' existing colours and materials exactly as they are in the photo — the'
      + ' colour scheme does not apply to them.'
    : '';

  // ── LABELLED SECTIONS, NOT ONE FLAT PARAGRAPH ──────────────────────────────
  // The model is still flux-kontext-max (see functions/generateImageFlux.js).
  // This restructuring was drafted while the staging flow was briefly pointed at
  // flux-2-pro; that swap was reverted and this was kept, because it turned out
  // to produce better results on Kontext than the flat version it replaced.
  //
  // WHICH IS NOT WHAT THE OLD COMMENTS HERE PREDICTED, and the prediction is
  // worth keeping honest about: the working theory was that Kontext weighted the
  // opening hardest, lost the fourth clause of any list, and read a long
  // constraint-heavy instruction as licence to change almost nothing — so the
  // file emitted one run-on paragraph ordered by what mattered most. Every scar
  // in the comments above came from a real failed generation, so the failures
  // were real. The conclusion drawn from them — that flatness was the fix — was
  // not. A HEADING is a stronger anchor than a good position in a paragraph: it
  // gives each rule a name, and a named block is something the output can be
  // checked against.
  //
  // What did NOT change: every guard. The car that anchored a scene, the door
  // that came back repainted six times, the table recoloured instead of
  // replaced, the empty-room contradiction that moved the walls — all of them
  // are still here, still in the words the failures were seen in. They are
  // sorted into sections rather than deleted. Restructuring a prompt is not a
  // reason to re-learn a fault it already taught us.
  //
  // EVERY OPTION THE READER SET IS CARRIED. Room type, state, style, palette,
  // custom colour pair, each of the five surface toggles in both positions, and
  // the free-text note. Nothing is summarised away, and each appears under the
  // heading it belongs to so none of them can be read as belonging to another.
  const section = (head, ...lines) => {
    const body = lines.filter(Boolean).join(' ');
    return body ? `${head}: ${body}` : '';
  };

  return [
    // ── 1. THE JOB ───────────────────────────────────────────────────────────
    // Named as RESTYLING, not as generating a room. Asking for "a magazine
    // photograph of a bedroom" invites a new bedroom; asking to restyle THIS one
    // keeps the photograph as the subject.
    // Naming the style in the opening line is deliberate — Kontext weights the
    // start of an instruction most. With `auto` there is no name to give, so
    // the sentence says what the room IS and the brief below says how to
    // decide; "a auto (ai chooses) bedroom" is what interpolating blindly
    // would have produced.
    // ── "THIS EXACT ROOM" COMES FIRST, THE ROOM TYPE SECOND ──────────────────
    // It read "Restyle the interior design of this room photo into a luxury
    // modern bedroom", and when the photograph was plainly not a bedroom the
    // model resolved the contradiction the cheap way: it produced a bedroom
    // and threw the geometry away. A reader who picked a room type got back
    // somebody else's room.
    //
    // `auto` never had the problem, because it never names a type that can
    // conflict — which is exactly why it held the space when a named type did
    // not.
    //
    // So the sentence now anchors on the photograph and CONVERTS it. "Keep
    // this exact room and turn it into X" has no contradiction in it: the
    // walls, the proportions and the openings are the subject, and the room
    // type is what they are being used for.
    // ── THE ROOM TYPE NEVER APPEARS HERE ─────────────────────────────────────
    // This said "convert it into a bedroom", and a reader who picked a room
    // type still got a reshaped room: different proportions, a centred entrance
    // where theirs was off to one side, their stairs gone. "Convert into a
    // bedroom" is a request for a bedroom to EXIST, and the cheapest way to
    // produce one is to build it.
    //
    // The room type is not a fact about the room. It is a fact about the
    // FURNITURE — what this space is going to be used for — so it now appears
    // only in the furnishing sentence below, and no sentence anywhere asks for
    // a room of that kind to exist. The subject of the opening line is always
    // the photograph.
    // ── THE EDIT IS NAMED BEFORE ANYTHING ELSE ───────────────────────────────
    // Kontext only edits, so this is not strictly load-bearing the way it would
    // be on a model that also generates from text — but it costs one line and it
    // is the sentence that stops the forty lines below reading as a description
    // of a room to invent. "Replace the floor, empty the room, different pieces
    // entirely" is a perfectly good brief for a new room; saying up front that
    // the attached photograph is the subject removes that reading.
    //
    // It is also the guard that keeps this prompt safe if the staging model is
    // ever pointed at something that does both jobs.
    'EDIT THE ATTACHED PHOTOGRAPH. The photograph is the subject and the'
      + ' starting point. Return that same room, edited — never a newly'
      + ' generated room, never a different room, never a different view of it.',
    `TASK: restyle this exact room from the photo as`
      + ` ${style.desc ? `${article(style.label)} ${style.label.toLowerCase()}` : 'a beautifully designed'} interior.`,
    // "This is a redesign of the photographed room, not a new room" used to sit
    // here. The opening line now says "keep this exact room from the photo — its
    // walls, its proportions and its openings", which is the same claim made
    // concretely, so the vaguer restatement is gone. Two sentences saying one
    // thing is how the constraint share crept back up.

    // ── 2. WHAT MUST NOT CHANGE ──────────────────────────────────────────────
    // Stated positively wherever possible: "keep every window exactly as it is"
    // gives the model something to reproduce, where "do not add a window" leaves
    // it holding the words "add a window". The one negation left is the specific
    // failure being guarded against, so it is worth its place.
    // The two guards, one sentence each and moved AFTER the work below — see
    // the note above. An opening is a hole in a wall; a door is what hangs in
    // it. Freezing the first is what keeps the room the user's room, and
    // saying nothing here about the second is what lets the surface list
    // replace door and window units without being read as licence to move one.
    //
    // The opening rule is a COUNT, not a prohibition: "no new openings" is a
    // negation, and a negation is what the model was holding when it added a
    // door. A count is something to check the picture against.
    // The opening line no longer carries "keep this exact room" — the room type
    // was removed from it and the anchoring went with it — so this sentence
    // takes the whole job, and says the shape is the photograph's rather than
    // listing dimensions abstractly. A reader who picks a room type is asking
    // for different FURNITURE in their room, never for a different room.
    // ── THE SHAPE, AND THE CAMERA, AS TWO SEPARATE INSTRUCTIONS ──────────────
    // "…ceiling height and camera angle from the photo" had the camera as the
    // last item of a five-item list, which is the position everything else in
    // this file has had to be rescued from. They are different promises — one
    // about the room, one about where it is seen from — and a result can break
    // either without breaking the other.
    //
    // Both are stated as things to REPRODUCE and then checked against
    // something countable: a wall that is longer than the one opposite, an
    // edge that has moved. "Keep the perspective" is not checkable; "the same
    // corner meets the floor at the same point in the frame" is.
    section(
      'KEEP EXACTLY AS IN THE PHOTO',
      'This is the same room, not a new one: keep its exact shape and size — the'
        + ' same wall positions, the same wall lengths, the same ceiling height,'
        + ' the same floor area. If one wall is longer than another in the photo,'
        + ' it stays longer. Never squarer, wider, taller or more symmetrical'
        + ' than it really is.',
      'The camera does not move: the same viewpoint, the same eye height, the'
        + ' same lens and the same framing as the photo. Every wall, corner and'
        + ' edge that is visible in the photo must appear at the same place in the'
        + ' result, and nothing that is out of frame may come into it.',
    ),
    // ── THE HOLE IS FIXED; WHAT FILLS IT IS NOT ──────────────────────────────
    // This said "keep exactly the same number of DOORWAYS and windows … restyle
    // them", and it lands in sentence three, where Kontext weights hardest. Six
    // generations came back with the same door repainted: "keep the doorways
    // the same" was carried forward as "keep the doors", and the replacement
    // instruction nine sentences later never overturned it.
    //
    // So the constraint now names only the APERTURE — a hole in a wall — and
    // licenses the change in the same breath, at the point the reader (and the
    // model) is deciding what "fixed" covers. A permission granted next to the
    // restriction cannot be lost between them.
    section(
      'WALL OPENINGS',
      'The wall openings are fixed: the same number of them as the photo, in the'
        + ' same walls, at the same positions and sizes. Never move, add or remove'
        + ' an opening. What FILLS each opening — the door or window unit itself —'
        + ' may be replaced entirely.',
    ),

    // ── 3. THE FURNITURE ─────────────────────────────────────────────────────
    // TWO STEPS FOR A FURNISHED ROOM, and they have to be two.
    //
    // "Replace all of it with new ones" was read as permission to recolour: the
    // same bed, the same wardrobe, the same rug, in a different shade. Splitting
    // it into EMPTY THE ROOM and then FURNISH THE EMPTY ROOM is what makes it a
    // replacement — the model has to picture the bare room in between, and at
    // that point there is no old furniture left to tint.
    //
    // The last sentence names the failure directly, because "new furniture" is
    // satisfied by a recolour and "different shapes and materials" is not.
    // VEHICLES ARE NAMED. The list said "furniture, rugs, curtains… clutter",
    // and a car parked in a hallway is none of those — so it stayed, and while
    // it stayed the model read the scene as "a garage with a car in it" and
    // declined to redesign around it. The same photograph with the car removed
    // came back properly renovated. It is the single largest object in frame;
    // if it is not named it anchors everything.
    // ── EMPTYING IS ITS OWN SENTENCE NOW, AND IT NAMES PEOPLE ────────────────
    // It was a clause inside a longer instruction, and a real generation
    // ignored all of it: the car, the dining table, the television and the man
    // sitting at the table all survived into the result, restyled rather than
    // removed. Anything not named is treated as part of the room.
    //
    // PEOPLE especially. Nothing had ever mentioned them, and a person in the
    // output of an interior render is not a staging choice — it is somebody's
    // relative left sitting in a marketing image.
    // WHERE THE ROOM TYPE LIVES NOW: "furnish it AS a bedroom" — a statement
    // about what goes in the space, not about what the space is. See the note
    // on the opening line.
    section(
      'FURNITURE',
      isEmpty
        ? `The room is empty: furnish it ${asRoom}with ${furnishWith}.`
        : 'Start by emptying the room completely. Nothing that is in the photo now'
          + ' may appear in the result: no people, no cars or vehicles, no'
          + ' furniture, appliances, televisions, stoves, rugs, textiles, bags,'
          + ' buckets, cables or personal belongings. The room must be bare before'
          + ' you begin.',
      // THE SECOND THING THAT SURVIVED FIVE GENERATIONS. The same table and the
      // same chairs came back in a new colour every time, because "all-new
      // pieces, not the originals recoloured" is an adjective and the shapes
      // were already there to trace. Naming the SHAPE as what must differ is
      // what the surfaces needed too.
      isEmpty
        ? ''
        : `Then furnish that bare room ${asRoom}with ${furnishWith}. Different pieces`
          + ' entirely — different shapes, different frames, different materials'
          + ' from the ones in the photo. Not the same table and chairs in a new'
          + ' colour.',
    ),

    // ── 4. THE SURFACES ──────────────────────────────────────────────────────
    // One sentence listing what changes, one listing what does not. Five
    // paragraphs of surface policy was most of why the instruction was too
    // long to act on.
    // ── STYLE AND COLOUR BEFORE THE SURFACES ─────────────────────────────────
    // They used to come after. "New wall surfaces: panelling, slatted timber,
    // cladding…" was therefore read with no idea what colour any of it should
    // be, and the model picked from the STYLE's own colour words — "marble and
    // brass accents" — which sat closer to it than the reader's chosen green.
    // Deciding the palette first and the materials second is also the order a
    // person designing a room works in.
    // STILL BEFORE THE SURFACES. The reason is unchanged by the model swap:
    // "new wall surfaces: panelling, slatted timber, cladding…" read with no
    // palette decided yet takes its colours from the STYLE's own colour words,
    // which sit closer to it than the reader's chosen green.
    section('STYLE', style.desc ? `${style.desc}.` : ''),
    section('COLOURS', colour.replace(/^Colours: /, '')),

    // JOINED WITH SPACES, because each entry is a whole sentence. Joining with
    // commas is what produced the ninety-word run in which the door was the
    // fourth clause and never got made.
    //
    // THE ONE SENTENCE THAT MAKES THE LIST MEAN ANYTHING: a generation asked to
    // replace the floor and the walls returned both in a slightly different
    // grey — the same concrete, the same plaster, repainted. Every "replace"
    // instruction in this prompt has needed the same clarification.
    section(
      'REPLACE THESE SURFACES',
      mayChange.length ? mayChange.map((s) => s.change).join(' ') : '',
      mayChange.length ? 'A repaint is not a replacement.' : '',
    ),

    // ── THE UNTICKED BOXES GET THEIR OWN HEADING ─────────────────────────────
    // This was one sentence sitting between two others, and it is the half of
    // the surface choice the reader is most likely to notice being ignored —
    // they actively unticked something. Under its own label it is checkable
    // against the output rather than being one clause among forty.
    section('LEAVE COMPLETELY UNTOUCHED', keepLine),

    // Style and colour used to be stated HERE, after the surfaces. They now
    // come before them — see the note above the surface list.

    // THE DODGE THIS CLOSES, seen in a real generation: asked for "luxury
    // modern" with a "bold dark accent", the model repainted the CAR black and
    // left the room untouched. It had satisfied the words at the lowest
    // possible cost. Naming where the palette applies is what makes that
    // reading unavailable.
    // BUILT FROM THE TICKED SURFACES, not a fixed list. It used to name "walls,
    // floor, ceiling" outright, which told the model to colour a ceiling the
    // reader had explicitly excluded. Furniture and textiles are always in
    // scope — they are new pieces either way, so there is nothing of the
    // reader's to protect.
    section(
      'SCOPE OF THE SCHEME',
      `Apply the style and colours to the room itself — ${
        [...mayChange.map((s) => s.keep.replace(/^the /, '')), 'furniture', 'textiles']
          .join(', ')
      }. Recolouring one object is not a redesign.`,
    ),

    section('ALSO INCLUDE', notes ? `${notes}.` : ''),

    // ── 6. THE STANDARD ──────────────────────────────────────────────────────
    // "Interior design" is an abstraction a model can satisfy with paint; a
    // short list of things that must be IN the picture is not.
    section(
      'QUALITY',
      'Make it look professionally designed: a real furniture layout, layered'
        + ' lighting, quality materials (wood, stone, metal, fabric), art and'
        + ' greenery. Every surface listed above must visibly change.',
      'Photorealistic interior photography, natural light, everything at true'
        + ' scale for the room.',
    ),

    // ── LAST WORD, SAME AS THE FIRST ─────────────────────────────────────────
    // The opening says this is an edit; forty lines of "replace the floor, empty
    // the room, different pieces entirely" is a long way to travel from it, and
    // all of that reads perfectly well as a description of a room to invent.
    // Closing on the same claim costs one line and means the instruction cannot
    // be read start-to-finish as a generation brief.
    'Every part of this describes an EDIT of the attached photograph. The walls,'
      + ' proportions, openings and viewpoint of that photograph are the ones in'
      + ' the result. Do not generate a different room.',
  ].filter(Boolean).join('\n\n');
}
